import { useState } from "react";

import { useRootStore } from "store";
import { dateFormat } from "common/dateFormat";
import { SortOrder } from "common/SortOrder";
import { Icon, SortIcon } from "components/icon/Icon";
import { formatFileSize } from "libs/file-size-format";
import { Tooltip } from "components/tooltip/Tooltip";
import Modal from "components/modal/Modal";
import { Button } from "components/button/Button";
import { DeliveryFile } from "models/DeliveryFile";
import {
  SortByCategory,
  SortByComment,
  SortByCreatedBy,
  SortByDate,
  SortByName,
  SortBySize,
  SortDeliveryUploadFile
} from "..";

export function DocumentDeliveryTable() {
  const [sortFunction, setSorting] = useState<SortDeliveryUploadFile>(
    () => SortByName
  );
  const [sortOrder, setSortOrder] = useState(SortOrder.Descending);
  const setSortingFn = (selectedSortFn: SortDeliveryUploadFile) => {
    setSortOrder(
      sortFunction === selectedSortFn && sortOrder === SortOrder.Descending
        ? SortOrder.Ascending
        : SortOrder.Descending
    );
    setSorting(() => selectedSortFn);
  };
  const [showRemoveFileModal, setShowRemoveFileModal] = useState(false);

  const [selectedFile, setSelectedFile] = useState<DeliveryFile | undefined>(
    undefined
  );

  const { workingCustomerId } = useRootStore((state) => state.global);
  const {
    fetchUploadedFiles,
    deleteDocuments,
    uploadedDocuments: filesUploaded
  } = useRootStore((state) => state.delivery);

  const { downloadFile } = useRootStore((state) => state.archive);

  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(false);

  const openRemoveFileModal = (file: DeliveryFile) => {
    setShowRemoveFileModal(true);
    setSelectedFile(file);
  };

  const deleteFile = async () => {
    setDeleteButtonDisabled(true);
    await deleteDocuments(workingCustomerId, selectedFile?.id ?? "");
    setShowRemoveFileModal(false);
    fetchUploadedFiles(workingCustomerId);
    setDeleteButtonDisabled(false);
  };

  const getModal = () => (
    <Modal
      header={<h1 className="m-0">Ta bort fil permanent?</h1>}
      onDismiss={() => setShowRemoveFileModal(false)}
      modalTitle="remove file"
      size="sm"
    >
      <div>
        <p>Vill du ta bort filen {selectedFile?.name} permanent?</p>
        <div className="d-flex justify-content-end">
          <Button
            className="mr-3"
            disabled={deleteButtonDisabled}
            onClick={() => deleteFile()}
          >
            Bekräfta
          </Button>
          <Button
            variant="outline"
            color="light"
            onClick={() => setShowRemoveFileModal(false)}
          >
            Avbryt
          </Button>
        </div>
      </div>
    </Modal>
  );

  const canDeleteUploadedFile = (file: DeliveryFile) =>
    Date.now() <= new Date(file.createdAt).getTime() + 43200000;

  const getLabelAndIconContent = (file: DeliveryFile) => {
    if (file.handled) {
      return {
        label: "Filen har hanterats",
        icon: <Icon name="check" />
      };
    }
    if (canDeleteUploadedFile(file)) {
      return {
        label: "Ta bort filen",
        icon: <Icon name="trash-o" onClick={() => openRemoveFileModal(file)} />
      };
    }
    return {
      label: "Filraderingsperioden har löpt ut",
      icon: <Icon name="remove" />
    };
  };

  const getTableRow = (file: DeliveryFile) => {
    const { label, icon } = getLabelAndIconContent(file);
    return (
      <tr key={file.id}>
        <td>{file.name}</td>
        <td>{formatFileSize(parseInt(file.fileSize))}</td>
        <td>{dateFormat(file.createdAt)}</td>
        <td>{file.category}</td>
        <td>{file.comment}</td>
        <td>{file.createdByName}</td>
        <td>
          <div className="d-flex fg-2">
            <Tooltip
              className="mr-3"
              content={<p className="m-0">Ladda ner filen</p>}
            >
              <Icon name="download" onClick={() => downloadFile(file)} />
            </Tooltip>
            <Tooltip className="mr-3" content={<p className="m-0">{label}</p>}>
              {icon}
            </Tooltip>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <h1>Uppladdade filer</h1>
      <hr className={"header-border"} />

      <table>
        <thead>
          <tr>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByName)}
            >
              <div className="d-flex">
                Namn
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByName ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortBySize)}
            >
              <div className="d-flex">
                Filstorlek
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortBySize ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByDate)}
            >
              <div className="d-flex">
                Uppladdat den
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByDate ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByCategory)}
            >
              <div className="d-flex">
                Kategori
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByCategory ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByComment)}
            >
              <div className="d-flex">
                Kommentar
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByComment ? sortOrder : ""}
                />
              </div>
            </th>
            <th
              className="cursor-pointer"
              onClick={() => setSortingFn(SortByCreatedBy)}
            >
              <div className="d-flex">
                Uppladdat av
                <SortIcon
                  className="ml-2"
                  sortOrder={sortFunction === SortByCreatedBy ? sortOrder : ""}
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filesUploaded
            .sort((a, b) => {
              return (
                sortFunction(a, b) *
                (sortOrder === SortOrder.Descending ? 1 : -1)
              );
            })
            .map((file) => getTableRow(file))}
        </tbody>
      </table>

      {showRemoveFileModal && getModal()}
    </>
  );
}
